import {updateTrackingConfiguration} from '@js/actions/trackingActions';

import PWAManager from '@js/modules/pwaManager';
import {hasBotCharacteristics} from '@js/modules/botDetection';


class TrackingManager {
    _initialTrackingDelay = 750; // in ms, settle delay before starting the dwell timer
    _dwellDelay = 2000;          // in ms, foreground time before a no-interaction visit is confirmed

    // Genuine human interactions that confirm a visit.
    _engagementEvents = ['pointerdown', 'pointermove', 'keydown', 'scroll', 'wheel', 'touchstart'];
    _confirmed = false;

    // A page load creates no visit: the pages seen until the confirmation go with it (landing page,
    // referrer, page views). Session storage, one visit at a time, a few pages at most.
    _pendingKey = 'pendingPages';
    _maxPendingPages = 10;

    /* Public methods
    ******************** */
    // A visit is confirmed as human only on a genuine signal — a trusted user
    // interaction, or enough foreground dwell time — never merely because the
    // page loaded. Headless bots that just load and leave stay unconfirmed
    // (validated:false server-side). Cookie refusal / adblock are NOT treated as
    // bot signals: a real user who rejects cookies still counts.
    initialize = (forceRequest) => {
        if (!this._alreadySent()) this._rememberPage();

        // A consent accept/deny is itself a genuine human interaction.
        if (forceRequest) {
            this._confirmVisit();
            return;
        }

        if (this._alreadySent()) return;

        // Confirm on the first genuine (trusted) interaction...
        this._bindEngagement();

        // ...or after enough foreground dwell time, unless the client shows
        // strong bot characteristics — then only a trusted interaction confirms.
        setTimeout(() => {
            if (this._confirmed || hasBotCharacteristics()) return;

            const focused = typeof document.hasFocus !== 'function' || document.hasFocus();

            if (document.visibilityState === 'visible' && focused) {
                this._confirmVisit();
            }
        }, this._initialTrackingDelay + this._dwellDelay);
    };

    // An event the page fires by itself waits for the confirmation, on its page, never creating the
    // visit. False once the visit is confirmed: the caller sends it.
    trackPageEvent = (name, props = {}) => {
        if (this._confirmed || this._alreadySent()) return false;

        const event = {name, props};
        const url = window.location.href.slice(0, 1000);
        try {
            const pages = this._pendingPages();
            // A reverse loop, not findLast (Safari 15.4+).
            let page = null;
            for (let index = pages.length - 1; index >= 0 && !page; index -= 1) {
                if (pages[index].url === url) page = pages[index];
            }
            if (page) {
                page.props = {...page.props, page_events: [...(page.props?.page_events || []), event]};
                window.sessionStorage.setItem(this._pendingKey, JSON.stringify({visit: this._visitToken() || '', pages}));
                return true;
            }
        } catch {
            // Storage unavailable: kept on the page's own properties below.
        }
        // Page not remembered yet: its properties carry the event when it is.
        window.pageTracking = {...(window.pageTracking || {}), page_events: [...(window.pageTracking?.page_events || []), event]};
        return true;
    };

    /* Private methods
    ******************** */
    _onEngagement = (event) => {
        // Ignore synthetic events dispatched by bots.
        if (event && event.isTrusted === false) return;

        this._confirmVisit();
    };

    _bindEngagement = () => {
        // No { once: true }: an untrusted (bot) event would otherwise consume the
        // listener before a later trusted one. Removed on confirmation instead.
        this._engagementEvents.forEach((name) => window.addEventListener(name, this._onEngagement, {capture: true, passive: true}));
    };

    _unbindEngagement = () => {
        this._engagementEvents.forEach((name) => window.removeEventListener(name, this._onEngagement, true));
    };

    // Send the visit confirmation to the server (validated = true).
    _confirmVisit = () => {
        if (this._confirmed) return;

        this._confirmed = true;
        this._unbindEngagement();

        if (this._alreadySent()) return;

        const properties = {
            valid: true
        };

        if (PWAManager.getPWADisplayMode() === PWAManager.MODE.PWA) {
            properties.pwa = true;
        }

        const pages = this._pendingPages();
        if (pages.length) {
            properties.pages = JSON.stringify(pages);
            // The page times are on this device's clock: the server reads them against it.
            properties.now = Date.now();
        }
        this._forgetPages();

        // The visit being confirmed, read now: by the time the beacon answers, the cookie may be the next visit's.
        const visit = this._visitToken();

        updateTrackingConfiguration(properties)
            .then(() => {
                if (!this._rejectCookies()) {
                    this._markVisit(visit);
                }
            })
            .catch(() => {
                if (!this._rejectCookies()) {
                    document.cookie = '';
                }
            });

        if (!this._rejectCookies()) {
            this._markVisit(visit);
        }
    };

    _pendingPages = () => {
        try {
            const pending = JSON.parse(window.sessionStorage.getItem(this._pendingKey) || 'null');
            return pending?.visit === (this._visitToken() || '') ? pending.pages : [];
        } catch {
            return [];
        }
    };

    _rememberPage = () => {
        const page = {
            url: window.location.href.slice(0, 1000),
            referrer: document.referrer.slice(0, 1000),
            at: Math.round(performance.timeOrigin || Date.now()),
            props: window.pageTracking || {}
        };

        try {
            const pending = this._pendingPages();
            // A tracked link that redirected (ApplicationController#remember_redirected_landing) is the first page.
            const redirected = document.cookie.match(/(?:^|; )pending_landing=([^;]*)/);
            if (redirected) {
                document.cookie = 'pending_landing=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
                if (!pending.length) {
                    pending.push({url: decodeURIComponent(redirected[1]).slice(0, 1000), referrer: '', at: page.at, props: {}});
                }
            }
            const pages = [...pending, page].slice(0, this._maxPendingPages);
            window.sessionStorage.setItem(this._pendingKey, JSON.stringify({visit: this._visitToken() || '', pages}));
        } catch {
            // Storage unavailable: the confirmation still creates the visit, on the current page.
        }
    };

    _forgetPages = () => {
        try {
            window.sessionStorage.removeItem(this._pendingKey);
        } catch {
            // Storage unavailable: nothing kept.
        }
    };

    // The marker holds Ahoy's visit token: a bare session cookie outlived the 12 h visit, and the
    // visitor's next visit was never confirmed.
    _visitToken = () => {
        const match = document.cookie.match(/(?:^|; )ahoy_visit=([^;]*)/);
        return match ? match[1] : null;
    };

    _markVisit = (visit) => {
        if (visit) document.cookie = `configurationSet=${visit};path=/`;
    };

    _rejectCookies = () => {
        if (navigator.cookieEnabled) return false;
        if (!document.cookie.indexOf) return false;

        // set and read cookie
        document.cookie = 'cookietest=1';
        const ret = document.cookie.indexOf('cookietest=') === -1;

        // delete cookie
        document.cookie = 'cookietest=1; expires=Thu, 01-Jan-1970 00:00:01 GMT';

        return ret;
    };

    _alreadySent = () => {
        if (this._rejectCookies()) return false;

        const visit = this._visitToken();
        const marker = document.cookie.match(/(?:^|; )configurationSet=([^;]*)/);
        return visit !== null && marker !== null && marker[1] === visit;
    };
}

export default new TrackingManager();
